<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue'
import { BookOpenText, Info, LayoutGrid, LibraryBig, Medal, Pencil, Pickaxe, ShoppingCart, TicketPlus, WandSparkles } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { usePreventNav } from '@/composables/usePreventNav'

interface Props {
	mobile?: boolean
	quickAccess?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	mobile: false,
	quickAccess: false,
})

const { t } = useI18n()
const authStore = useAuthStore()
const { routeToTweak } = usePreventNav()
const route = useRoute()

const css = computed(() => {
	return props.mobile ? 'menu-link w-full !text-base !justify-start' : 'menu-link lg:flex-row flex-col'
})
</script>

<template>
	<div
		:class="{
			'gap-1 !hidden menu:!flex': !mobile && !quickAccess,
			'flex flex-col w-full items-start gap-2': mobile,
			'flex flex-row': quickAccess,
		}"
	>
		<router-link
			:to="{ path: '/explore', query: route.query }"
			:class="css"
		>
			<LayoutGrid class="lucide-button-icon" />
			{{ t('menu.explore') }}
		</router-link>
		<router-link
			to="/collection"
			:class="css"
		>
			<LibraryBig class="lucide-button-icon" />
			{{ t('menu.collection') }}
		</router-link>
		<router-link
			to="/creations"
			:class="[css, {
				'menuxxl:!flex !hidden': !quickAccess && !mobile,
			}]"
		>
			<Pickaxe class="lucide-button-icon" />
			{{ t('menu.creations') }}
		</router-link>
		<router-link
			to="/create"
			:class="css"
		>
			<WandSparkles class="lucide-button-icon" />
			{{ t('common.create') }}
		</router-link>
		<router-link
			:to="authStore.isAuth() ? { path: '/tweak', query: route.query } : '/tweak'"
			:class="[css, { '!bg-transparent': !authStore.isAuth() }]"
			@click.prevent="routeToTweak"
		>
			<Pencil class="lucide-button-icon" />
			{{ t('common.tweak') }}
		</router-link>

		<template v-if="mobile">
			<Divider layout="horizontal" />
			<router-link
				to="/daily-tasks"
				:class="[css, { '!bg-transparent': !authStore.isAuth() }]"
			>
				<TicketPlus class="lucide-button-icon" />
				{{ t('daily_tasks.title') }}
			</router-link>
			<router-link
				to="/guide"
				:class="[css, { '!bg-transparent': !authStore.isAuth() }]"
			>
				<BookOpenText class="lucide-button-icon" />
				{{ t('common.guide') }}
			</router-link>
			<router-link
				to="/leaderboard"
				:class="[css, { '!bg-transparent': !authStore.isAuth() }]"
			>
				<Medal class="lucide-button-icon" />
				{{ t('menu.leaderboard') }}
			</router-link>
			<router-link
				to="/tickets"
				:class="[css, { '!bg-transparent': !authStore.isAuth() }]"
			>
				<ShoppingCart class="lucide-button-icon" />
				{{ t('menu.shop') }}
			</router-link>
			<router-link
				to="/about"
				:class="[css, { '!bg-transparent': !authStore.isAuth() }]"
			>
				<Info class="lucide-button-icon" />
				{{ t('menu.about') }}
			</router-link>
		</template>
	</div>
</template>

<style lang="scss">
.menu-link {
  @apply no-underline text-white gap-1 duration-300 rounded-xl p-2 bg-transparent border-none hover:bg-purple-600 font-extrabold flex items-center justify-center xsp:text-xs text-[0.6rem];
}

.router-link-active.navigation-link,  {
    @apply  bg-gradient-to-r  hover:to-purple-700 hover:bg-gradient-to-br;
}

.router-link-active.menu-link,
.router-link-active.menu-link-mobile  {
    @apply  bg-purple-600;
}
</style>
